import type { ActionReducerMapBuilder, PayloadAction } from "@reduxjs/toolkit";
import { supportLogout, userLogout } from "../LoginSlice";

/**
 * Shared parts of RecommenderSlice.
 *
 * As of the Suggestive Selling convergence ALL SEVEN reducers are here: mobile used to carry only
 * five, and the two it lacked — `recommenderConfigCallComplete` and `getRecommendationsV2` — were
 * exactly what stopped it running the web flow. Both platforms now dispatch the same actions into
 * the same saga.
 *
 * The logout `extraReducers` are shared too, as of 2026-09-03: `userLogout` / `supportLogout`
 * moved into `LoginSlice/shared.ts`, so both platforms have the actions and both clear the
 * recommender config on logout. This is the FIRST slice on the other platform to case on them —
 * every other native slice still does not, deliberately (see `LoginSlice/shared.ts`).
 *
 * ⚠️ The action param is spelled `actions` (plural) in both apps. Kept verbatim — it is
 * positional, so the name carries nothing, but there is no reason to churn it.
 *
 * ⚠️ It must be `PayloadAction<any>`, never `any` — see the ledger.
 */
export interface SharedProps {
  recommenderConfig: Record<string, any>;
  recommendations: {
    items: string[];
    recommendationId: string;
  } | {};
  fetchingRecommendations: boolean;
}

export const sharedInitialState: SharedProps = {
  recommenderConfig: {},
  recommendations: {},
  fetchingRecommendations: false,
};

/** Every reducer the slice has. Both platforms spread this whole object. */
export const sharedReducers = {
  getRecommenderConfig: (state: SharedProps, actions: PayloadAction<any>) => {},
  saveRecommenderConfig: (state: SharedProps, actions: PayloadAction<any>) => {
    state.recommenderConfig = actions.payload;
  },
  getRecommendations: (state: SharedProps, actions: PayloadAction<any>) => {
    state.fetchingRecommendations = true;
  },
  saveRecommendations: (state: SharedProps, actions: PayloadAction<any>) => {
    state.recommendations = actions.payload;
    state.fetchingRecommendations = false;
  },
  saveRecommendationsFailed: (state: SharedProps, actions: PayloadAction<any>) => {
    state.fetchingRecommendations = false;
  },
  /**
   * Fired in the config saga's `finally`, success or failure. It carries no payload: its whole
   * job is to tell whatever is waiting that the one-shot config call has settled, so a screen
   * does not decide "suggestive selling is off" while the answer is still in flight.
   */
  recommenderConfigCallComplete: (state: SharedProps) => {},
  getRecommendationsV2: (state: SharedProps, actions: PayloadAction<any>) => {
    state.fetchingRecommendations = true;
  },
};

/**
 * Clear the recommender config on either kind of logout.
 *
 * Both cases have REAL bodies and both do the same thing: the config is per-account, so a
 * different user (or a support session ending) must not inherit the previous one's engine
 * settings. Kept as two explicit cases rather than a matcher — that is the shape every other
 * slice uses, and the two are separate actions with separate meanings.
 */
export const sharedExtraReducers = (builder: ActionReducerMapBuilder<SharedProps>) => {
  builder
    .addCase(userLogout, (state) => {
      state.recommenderConfig = {};
    })
    .addCase(supportLogout, (state) => {
      state.recommenderConfig = {};
    });
};
